import { Button, Form, Space } from "antd";
import { useEffect, useState } from "react";
import { useAppSelector } from "../../../../app/store";
import WarningIcon from "../../../../assets/icons/WarningIcon";
import CustomModal from "../../../../components/ui/CustomModal";
import { mailRegex } from "../../../../constants/constants";
import { useSendInviteMutation } from "../../../../features/emp-setting/employee/employeeApi";
import { useGetAllAssignProjectQuery } from "../../../../features/portal/companyOnboard/companyOnboardApi";
import useError from "../../../../hooks/useError";
import useModal from "../../../../hooks/useModal";
import { ModalComponentProps } from "../../../../types";
import { isValidTLD } from "../../../../utils/utils";
import InviteConfirmModal from "./InviteConfirmModal";
import InviteSuccessModal from "./InviteSuccessModal";

export default function InviteEmployeeModal({
  handleClose,
  ...rest
}: ModalComponentProps) {
  const [form] = Form.useForm();
  const [emails, setEmails] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState<string>("");
  const [invalidEmails, setInvalidEmails] = useState<string[]>([]);
  const [loadingYes, setLoadingYes] = useState(false);
  const [loadingNo, setLoadingNo] = useState(false);
  interface ErrorDownloadItem {
    email: string;
    error: string;
  }
  const [errorDownload, setErrorDownload] = useState<
    ErrorDownloadItem[] | null
  >(null);
  const [hasHandledError, setHasHandledError] = useState(false);
  const [downloadModalMeta, setDownloadModalMeta] = useState<{
    title: string;
    subTitle: string;
  }>({
    title: "Some Email Already Exists!",
    subTitle:
      "The following email addresses are already registered in DigiGO. You can download the list to review before sending new invitations.",
  });
  const { open, handleOpen, handleClose: handleChildClose } = useModal();
  const {
    open: openConfirmAccessModal,
    handleOpen: handleConfirmAccessOpen,
    handleClose: handleConfirmAccessClose,
  } = useModal<any, "CONFIRM_ACCESS">();

  const {
    open: openConfirmDownloadModal,
    handleOpen: handleConfirmDownloadOpen,
    handleClose: handleConfirmDownloadClose,
  } = useModal<any, "CONFIRM_DOWNLOAD">();

  const [sendInvite, { data: inviteResponse, isLoading, isError, error, isSuccess }] =
    useSendInviteMutation();
  useError(isError, error, "Fail to send invite");
  const { user } = useAppSelector((state) => state.auth);

  const {
    isError: getAllAssignProjectIsError,
    error: getAllAssignProjectError,
    data: getAllAssignProject,
  } = useGetAllAssignProjectQuery(
    { id: user?.data?.company_id as number },
    {
      skip: !user?.data?.company_id, // only run when companyId exists
    },
  );

  useError(
    getAllAssignProjectIsError,
    getAllAssignProjectError,
    "Get all assign project data fail",
  );
  // ------------ ALERT -------------------
  // Avoid hardcoded project IDs; detect DigiGO access by service/name.
  const hasDigigoProject =
    getAllAssignProject?.data?.projects?.some((project) => {
      const serviceName = (project.service_name || "").toLowerCase();
      const projectName = (project.project_name || "").toLowerCase();
      return serviceName === "digigo" || projectName.includes("digigo");
    }) || false;

  console.log("error:", error);
  console.log("errorDownload:", errorDownload);

  const isDuplicateInviteError = (message?: string) => {
    const text = (message || "").toLowerCase();
    return (
      text.includes("already exists") ||
      text.includes("already registered") ||
      text.includes("already in digigo")
    );
  };

  const prepareDownloadModal = (responseErrors: ErrorDownloadItem[]) => {
    const hasErrors = Array.isArray(responseErrors) && responseErrors.length > 0;
    if (!hasErrors) return;

    const allAreDuplicate = responseErrors.every((item) =>
      isDuplicateInviteError(item?.error),
    );

    if (allAreDuplicate) {
      setDownloadModalMeta({
        title: "Some Email Already Exists!",
        subTitle:
          "The following email addresses are already registered in DigiGO. You can download the list to review before sending new invitations.",
      });
      return;
    }

    setDownloadModalMeta({
      title: "Some Invitations Failed",
      subTitle:
        "Some invitations could not be processed. Download the list to see the exact reason for each email.",
    });
  };

  const onFinish = () => {
    if (hasDigigoProject) {
      handleConfirmAccessOpen({ type: "CONFIRM_ACCESS" });
    } else {
      sendInvite({
        email: emails,
        company_id: user?.data?.company_id,
        is_digigo_access: 0,
      });
    }
  };

  const handleAccess = (accessValue: number) => {
    if (accessValue === 1) {
      setLoadingYes(true);
      setLoadingNo(false);
    } else {
      setLoadingNo(true);
      setLoadingYes(false);
    }

    sendInvite({
      email: emails,
      company_id: user?.data?.company_id,
      is_digigo_access: accessValue,
    });
  };

  const handleKeyDown = async (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    try {
      await form.validateFields();

      if (e.key === "Enter" || e.key === "," || e.key === " ") {
        e.preventDefault();
        processEmails(inputValue);
        if (inputValue.trim() && !emails.includes(inputValue.trim())) {
          setEmails([...emails, inputValue.trim()]);
          setInputValue("");
        }
      }

      // Backspace to remove the last tag when the input is empty
      if (e.key === "Backspace" && inputValue === "" && emails.length > 0) {
        e.preventDefault();
        setEmails(emails.slice(0, -1));
      }
    } catch (err) {}
  };

  const handleRemoveTag = (index: number) => {
    setEmails(emails.filter((_, i) => i !== index));
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData("text");
    if (typeof pastedText !== "string" || !pastedText.trim()) return;
    processEmails(pastedText);
  };

  const processEmails = (text: string) => {
    if (!text.trim()) return;

    // Split emails by commas, spaces, or new lines
    const emailList = text
      .split(/[\s,]+/)
      .map((email) => email.trim())
      .filter((email) => email !== "");

    // Email
    // if (emails.length + emailList.length > 50) {
    //   setInvalidEmails([
    //     ...invalidEmails,
    //     "You can enter a maximum of 50 emails",
    //   ]);
    //   return;
    // }

    // Email limit check (if adding emails exceeds the limit)
    if (emails.length + emailList.length > 50) {
      setInvalidEmails(["You can enter a maximum of 50 emails"]);
      return;
    } else {
      // Clear the error message if emails drop below the limit
      setInvalidEmails([]);
    }

    const validEmails = emailList.filter((email) => mailRegex.test(email));
    const invalidList = emailList.filter((email) => !mailRegex.test(email));

    // setEmails([...emails, ...validEmails]);
    // setInvalidEmails([...invalidEmails, ...invalidList]);

    // Add valid emails and filter out duplicates
    setEmails((prevEmails) => {
      const newEmails = [...prevEmails, ...validEmails];
      return newEmails.slice(0, 50); // Ensure no more than 50 emails
    });

    // Set the invalid emails state
    setInvalidEmails((prevInvalidEmails) => [
      ...prevInvalidEmails,
      ...invalidList,
    ]);

    // Update form field value to avoid validation errors
    form.setFieldsValue({ emails: [...emails, ...validEmails] });

    // Revalidate the field to remove any previous validation messages
    form.validateFields(["emails"]);

    // Filter out already added emails
    const newEmails = emailList.filter(
      (email) => !emails.includes(email) && mailRegex.test(email),
    );

    if (newEmails.length > 0) {
      setEmails([...emails, ...newEmails]);
    }

    setInputValue(""); // Clear input field
  };

  const handleDownload = () => {
    if (!errorDownload || errorDownload === null) return;

    // Use CRLF (\r\n) for Windows Notepad, and don't add extra join separators
    const textContent = errorDownload
      .map(
        (item) =>
          `Email: ${item.email}\r\nError Message: ${item.error}\r\n\r\n`,
      )
      .join(""); // join("") because each item already contains its trailing CRLFs

    const blob = new Blob([textContent], { type: "text/plain;charset=utf-8" });

    // 📥 Create a temporary download link
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "Existing_invite_mail_list.txt";
    link.click();

    // ♻️ Clean up
    URL.revokeObjectURL(link.href);

    // 🧹 Close handlers
    handleClose?.();
    handleChildClose?.();
    handleConfirmDownloadClose?.();
  };

  // success response can still include per-email processing errors
  useEffect(() => {
    if (!isSuccess) return;

    const responseErrors = (inviteResponse as any)?.data?.errors;
    if (Array.isArray(responseErrors) && responseErrors.length > 0) {
      prepareDownloadModal(responseErrors);
      setErrorDownload(responseErrors);
      handleConfirmDownloadOpen({ type: "CONFIRM_DOWNLOAD" });
      setHasHandledError(true);
      return;
    }

    handleOpen({});
  }, [handleConfirmDownloadOpen, handleOpen, inviteResponse, isSuccess]);

  useEffect(() => {
    if (
      !hasHandledError && // ✅ Prevent repeat
      isError &&
      typeof error === "object" &&
      error !== null &&
      "data" in error &&
      (error as any).data?.data?.errors?.length > 0
    ) {
      const responseErrors = (error as any).data?.data?.errors || [];
      prepareDownloadModal(responseErrors);
      handleConfirmDownloadOpen({ type: "CONFIRM_DOWNLOAD" });
      setErrorDownload(responseErrors);
      setHasHandledError(true); // ✅ Mark as handled
    }
  }, [isError, error, handleConfirmDownloadOpen, hasHandledError]);

  return (
    <>
      <CustomModal
        title="Invite Employee"
        {...rest}
        handleClose={handleClose}
        width={535}
        footer={null}
      >
        <InviteSuccessModal
          open={open.open}
          handleClose={() => {
            handleClose();
            handleChildClose();
            handleConfirmAccessClose();
          }}
        />
        <Form
          className="custom-form"
          layout="vertical"
          requiredMark="optional"
          size="large"
          onFinish={onFinish}
          form={form}
        >
          <Form.Item
            name="emails"
            label="Invite single and multiple employees"
            className="mb-[18px]"
            rules={[
              // {
              //   required: true,
              //   message: "Please enter at least one email",
              // },
              {
                validator: async (_: any, value: string | string[]) => {
                  if (!value || (Array.isArray(value) && value.length === 0)) {
                    return Promise.reject(
                      new Error("Please enter at least one email"),
                    );
                  }

                  // Ensure value is a string before proceeding
                  const emailString = Array.isArray(value)
                    ? value.join(", ")
                    : value;

                  // Split input into multiple emails using commas or spaces
                  const emailList = emailString
                    .split(/[\s,]+/)
                    .map((email) => email.trim())
                    .filter((email) => email !== "");

                  if (emailList.length === 0) {
                    return Promise.reject(
                      new Error("Please enter at least one email"),
                    );
                  }

                  // Validate each email
                  const invalidEmails = emailList.filter(
                    (email) => !mailRegex.test(email),
                  );

                  if (invalidEmails.length > 0) {
                    return Promise.reject(
                      new Error("Please enter a valid email"),
                    );
                  }

                  const invalidLTDEmails = emailList.filter(
                    (email) => !isValidTLD(email),
                  );

                  if (invalidLTDEmails.length > 0) {
                    return Promise.reject(
                      new Error("Please enter a valid TLD"),
                    );
                  }

                  return Promise.resolve();
                },
              },
            ]}
          >
            <div className="border rounded-md border-display-medium p-2.5">
              <div className="flex flex-wrap gap-1">
                {emails.map((tag, index) => (
                  <span
                    key={index}
                    className="bg-extra-light text-display-body font-medium px-1.5 py-0.5 rounded-md flex items-center space-x-1.5"
                  >
                    <span>{tag}</span>
                    <button
                      onClick={() => handleRemoveTag(index)}
                      className="text-sub-header hover:text-header"
                      type="button"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
              <textarea
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                rows={4}
                // className="w-full mt-1 focus:ring-0 focus:outline-none text-sub-header border-none placeholder:text-extra-gray text-base resize-none"
                className={`w-full mt-1 focus:ring-0 focus:outline-none border-none placeholder:text-extra-gray text-base resize-none ${
                  invalidEmails.length > 0 ? "border border-red-500" : ""
                }`}
                placeholder="Enter email addresses, separated by commas or spaces"
              />
            </div>

            {invalidEmails.length > 0 && (
              <p className="text-red-500 mt-1">{invalidEmails.join(", ")}</p>
            )}
          </Form.Item>

          <Space
            align="center"
            size={12}
            className="w-full px-4 py-1.5 rounded-md text-status-danger-text bg-[#fff4f4] max-h-9"
          >
            <WarningIcon width={24} height={24} />
            <div className="text-sm font-medium">
              Press enter to separate the email addresses
            </div>
          </Space>

          <Space className="w-full mt-3" size={12}>
            <Button
              className="normal-dark-button modal-button"
              onClick={handleClose}
              size="middle"
              disabled={isLoading}
              htmlType="button"
            >
              Cancel
            </Button>
            <Button
              className="modal-button"
              type="primary"
              htmlType="submit"
              size="middle"
              loading={
                openConfirmAccessModal?.type === "CONFIRM_ACCESS"
                  ? false
                  : isLoading
              }
              disabled={!emails.length}
            >
              Send Invitation
            </Button>
          </Space>
        </Form>
      </CustomModal>

      {openConfirmAccessModal?.type === "CONFIRM_ACCESS" && (
        <InviteConfirmModal
          title="DigiGO Access Alert!"
          subTitle="You’re about to give this employee access to the DigiGO system. Once access is granted, they’ll receive an invitation email to activate their account."
          open={openConfirmAccessModal?.open}
          handleClose={handleConfirmAccessClose}
        >
          <Space className="w-full mt-2" size={12}>
            <Button
              className="normal-dark-button modal-button !h-[40px]"
              size="middle"
              loading={loadingNo}
              onClick={() => handleAccess(0)}
            >
              Cancel
            </Button>

            <Button
              className="modal-button !h-[40px] !bg-orange-600"
              type="primary"
              size="middle"
              loading={loadingYes}
              onClick={() => handleAccess(1)}
            >
              Grant Access
            </Button>
          </Space>
        </InviteConfirmModal>
      )}

      {openConfirmDownloadModal?.type === "CONFIRM_DOWNLOAD" && (
        <InviteConfirmModal
          title={downloadModalMeta.title}
          subTitle={downloadModalMeta.subTitle}
          open={openConfirmDownloadModal?.open}
          handleClose={handleConfirmDownloadClose}
        >
          <Space className="w-full mt-2" size={12}>
            <Button
              className="normal-dark-button modal-button !h-[40px]"
              size="middle"
              onClick={() => {
                handleClose();
                handleChildClose();
                handleConfirmDownloadClose();
              }}
            >
              Cancel
            </Button>

            <Button
              className="modal-button !h-[40px] !bg-orange-600"
              type="primary"
              size="middle"
              onClick={handleDownload}
            >
              Download
            </Button>
          </Space>
        </InviteConfirmModal>
      )}
    </>
  );
}
